import type { EnemyState, Transform } from "@mucki/shared";
import * as THREE from "three";
import { createTransformBuffer } from "../systems/interpolation.js";

const CALM = new THREE.Color(0x8ce0a8);
const HOSTILE = new THREE.Color(0xf07a5a);

export interface EnemyMesh {
  group: THREE.Group;
  setState(state: EnemyState): void;
  update(delta: number): void;
  dispose(): void;
}

/** A forest sprite: a hovering glow with leaf-like wings, built from primitives. */
export function createEnemyMesh(): EnemyMesh {
  const group = new THREE.Group();
  const material = new THREE.MeshStandardMaterial({
    color: CALM,
    emissive: CALM,
    emissiveIntensity: 0.5,
    roughness: 0.3,
    transparent: true,
  });

  const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.45, 1), material);
  core.castShadow = true;
  group.add(core);

  const wings: THREE.Mesh[] = [];
  for (const side of [-1, 1]) {
    const wing = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.7, 5), material);
    wing.position.set(side * 0.4, 0.1, -0.1);
    wing.rotation.z = (side * Math.PI) / 3;
    group.add(wing);
    wings.push(wing);
  }

  let state: EnemyState = "idle";
  let clock = 0;

  return {
    group,
    setState: (next) => {
      if (next === state) return;
      state = next;
      const hostile = next === "chase" || next === "attack";
      material.color.copy(hostile ? HOSTILE : CALM);
      material.emissive.copy(hostile ? HOSTILE : CALM);
    },
    update: (delta) => {
      clock += delta;
      const flap = Math.sin(clock * 14) * 0.25;
      wings.forEach((wing, index) => {
        wing.rotation.y = index === 0 ? flap : -flap;
      });

      if (state === "dead") {
        material.opacity = Math.max(0.05, material.opacity - delta * 2);
        group.position.y = Math.max(0.1, group.position.y - delta * 1.5);
        return;
      }

      material.opacity = 1;
      group.position.y = 1.1 + Math.sin(clock * 2) * 0.12;
    },
    dispose: () => {
      group.traverse((object) => {
        if (object instanceof THREE.Mesh) object.geometry.dispose();
      });
      material.dispose();
      group.removeFromParent();
    },
  };
}

export interface RemoteEnemy {
  applySnapshot(transform: Transform, time: number): void;
  setState(state: EnemyState): void;
  update(renderTime: number, delta: number): void;
  dispose(): void;
}

export function createRemoteEnemy(scene: THREE.Scene, initial: Transform): RemoteEnemy {
  const mesh = createEnemyMesh();
  mesh.group.position.set(initial.x, initial.y, initial.z);
  scene.add(mesh.group);

  const buffer = createTransformBuffer(initial);

  return {
    applySnapshot: (transform, time) => buffer.push({ ...transform, time }),
    setState: (state) => mesh.setState(state),
    update: (renderTime, delta) => {
      const transform = buffer.sample(renderTime);
      mesh.group.position.x = transform.x;
      mesh.group.position.z = transform.z;
      mesh.group.rotation.y = transform.rotationY;
      // The hover height is animation, not state — update() owns position.y.
      mesh.update(delta);
    },
    dispose: () => mesh.dispose(),
  };
}
